import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useSelector } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ScriptableLineSegmentContext,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import styles from "./style.module.scss";
import AuthenticatedLayout from "../../components/Layout/authenticatedLayout";
import ImportsDataManager from "../../components/DataManagers/ImportsDataManager";
import RaceDataDataManager from "../../components/DataManagers/RaceDataDataManager";
import RESSDataManager from "../../components/DataManagers/RESSDataManager";
import OptimisationPbmsGraph from "../../components/RESS/OptimisationPbmsGraph";
import OptimisationTCellGraph from "../../components/RESS/OptimisationTCellGraph";
import { QUERY_RESS_RACE_PROFILE } from "../../utils/graphql/queries/ressQueries";
import { useQueryRessPitBoostEnergyAdjustments } from "../../utils/graphql/hooks/ressHooks";
import { RootState } from "../../state";
import { StrategyImport } from "../../types/imports";
import EnergySelectionGraphs from "./EnergySelectionGraphs";
import RaceBuilderInputs, { RaceBuilderFormState } from "./RaceBuilderInputs";
import RaceBuilderConfig from "./RaceBuilderConfig";
import RaceProfile, {
  RaceProfileData,
  RaceProfileDraft,
  raceProfileInputsChanged,
} from "./RaceProfile";
import RaceBuilderSimulation, {
  useRaceBuilderSimulation,
} from "./RaceBuilderSimulation";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);

function getRaceBuilderDefaults(
  strategy: StrategyImport | null,
): RaceBuilderFormState {
  const data = strategy?.data;
  const numberOrEmpty = (value: unknown): number | "" =>
    typeof value === "number" && Number.isFinite(value) ? value : "";

  return {
    dataFile:
      typeof data?.h5FileNameForRESSModel === "string"
        ? data.h5FileNameForRESSModel
        : "",
    totalRaceEnergykWh: numberOrEmpty(data?.EStartOfRace),
    numberOfLaps: numberOrEmpty(data?.NLapsRace),
    initialSocPct: 100,
    initialTCellC: numberOrEmpty(data?.TBMSCellMaxStartOfRace),
    ambientC: numberOrEmpty(data?.TAmbient),
    vCarScaler: 1,
    lastCornerBufferkWh: 0,
    lockingTargetkWh: numberOrEmpty(data?.ELapTargetLockedLeader),
    ucProfile: false,
    maxUnderconsumptionkWh: 0.5,
    pitBoostRace: false,
    targetPitSoc:
      typeof data?.targetPitSoc === "number" &&
      Number.isFinite(data.targetPitSoc) &&
      data.targetPitSoc >= 0 &&
      data.targetPitSoc <= 100
        ? data.targetPitSoc
        : 40,
    pitScenario: 3,
  };
}

const EMPTY_TRACE: number[] = [];

function RESSPage() {
  const [activeTab, setActiveTab] = useState<"race-builder" | "plots">(
    "race-builder",
  );
  const client = useApolloClient();
  const [profileLoading, setProfileLoading] = useState(false);
  const [raceProfile, setRaceProfile] = useState<RaceProfileDraft | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const profileRequestVersion = useRef(0);
  const temperatureChartRef =
    useRef<ChartJS<"line", { x: number; y: number }[]>>(null);
  const powerChartRef =
    useRef<ChartJS<"line", { x: number; y: number }[]>>(null);
  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy,
  );
  const strategyImportId = strategyImport?._id ?? null;
  const strategyImportDt =
    strategyImport && Number.isFinite(Number(strategyImport.dt))
      ? Number(strategyImport.dt)
      : null;
  const strategyImportVersion = strategyImportId
    ? `${strategyImportId}:${strategyImportDt ?? ""}`
    : null;
  const {
    data: pitBoostEnergyData,
    loading: pitBoostEnergyLoading,
    error: pitBoostEnergyError,
  } = useQueryRessPitBoostEnergyAdjustments(strategyImportId, strategyImportDt);
  const energyAdjustments =
    pitBoostEnergyData?.getRessPitBoostEnergyAdjustments;
  const currentEnergyAdjustments =
    !pitBoostEnergyLoading &&
    !pitBoostEnergyError &&
    energyAdjustments?.strategyImportId === strategyImportId &&
    energyAdjustments?.strategyImportDt === strategyImportDt
      ? energyAdjustments?.pitBoostEnergyAdjustmentskWh
      : undefined;
  const emData = useSelector(
    (state: RootState) => state.imports.imports.em?.data,
  );
  const pitBoostScenarios = [1, 2, 3].map((scenario) => {
    const stopTimeS = strategyImport?.data.tPitBoostVsScenario?.[scenario - 1];
    const energyKwh = strategyImport?.data.EPitBoostVsScenario?.[scenario - 1];
    return {
      scenario,
      stopTimeS:
        typeof stopTimeS === "number" &&
        Number.isFinite(stopTimeS) &&
        stopTimeS >= 0
          ? stopTimeS
          : null,
      energyKwh:
        typeof energyKwh === "number" && Number.isFinite(energyKwh)
          ? energyKwh
          : null,
    };
  });
  const segmentCount = emData?.NMaxSegment;
  const segmentEnds = emData?.sApexNextStaticVsNSegmentEM;
  const segmentEndSlapM: number[] | null =
    Number.isSafeInteger(segmentCount) &&
    segmentCount > 0 &&
    Array.isArray(segmentEnds) &&
    segmentEnds.length >= segmentCount &&
    segmentEnds
      .slice(0, segmentCount)
      .every(
        (value, index) =>
          typeof value === "number" &&
          Number.isFinite(value) &&
          (index === segmentCount - 1 ||
            value > (index === 0 ? 0 : segmentEnds[index - 1])),
      )
      ? segmentEnds.slice(0, segmentCount)
      : null;
  const strategyTCellMaxDerateStarts =
    strategyImport?.data?.TBMSCellMaxDerateStarts;
  const tCellMaxDerateStarts =
    typeof strategyTCellMaxDerateStarts === "number" &&
    Number.isFinite(strategyTCellMaxDerateStarts)
      ? strategyTCellMaxDerateStarts
      : null;
  // Store the scenario 3 total so scenario changes never compound adjustments.
  const [raceBuilderBaselineInputs, setRaceBuilderBaselineInputs] =
    useState<RaceBuilderFormState>(() =>
      getRaceBuilderDefaults(strategyImport),
    );
  const pitBoostEnergyAdjustmentKwh =
    !raceBuilderBaselineInputs.pitBoostRace ||
    raceBuilderBaselineInputs.pitScenario === 3
      ? 0
      : currentEnergyAdjustments?.[raceBuilderBaselineInputs.pitScenario - 1] ??
        null;
  const raceBuilderInputs = useMemo<RaceBuilderFormState>(
    () => ({
      ...raceBuilderBaselineInputs,
      totalRaceEnergykWh:
        raceBuilderBaselineInputs.totalRaceEnergykWh === "" ||
        pitBoostEnergyAdjustmentKwh === null
          ? ""
          : raceBuilderBaselineInputs.totalRaceEnergykWh +
            pitBoostEnergyAdjustmentKwh,
    }),
    [raceBuilderBaselineInputs, pitBoostEnergyAdjustmentKwh],
  );
  const raceEnergyError =
    pitBoostEnergyAdjustmentKwh === null
      ? pitBoostEnergyError
        ? `Could not load pit boost energies: ${pitBoostEnergyError.message}`
        : pitBoostEnergyLoading
        ? "Loading pit boost energies..."
        : "Cannot calculate total race energy: the strategy import needs valid boost energies for scenario 3 and the selected scenario."
      : raceBuilderInputs.totalRaceEnergykWh !== "" &&
        (!Number.isFinite(raceBuilderInputs.totalRaceEnergykWh) ||
          raceBuilderInputs.totalRaceEnergykWh < 0)
      ? "Total race energy must be a finite number greater than or equal to zero."
      : null;
  const ucProfileValid =
    !raceBuilderInputs.ucProfile ||
    (typeof raceBuilderInputs.lockingTargetkWh === "number" &&
      Number.isFinite(raceBuilderInputs.lockingTargetkWh) &&
      raceBuilderInputs.lockingTargetkWh > 0 &&
      Number.isFinite(raceBuilderInputs.maxUnderconsumptionkWh) &&
      raceBuilderInputs.maxUnderconsumptionkWh >= 0 &&
      raceBuilderInputs.maxUnderconsumptionkWh <= 1);
  const appliedStrategyVersion = useRef(strategyImportVersion);
  const builderSimulation = useRaceBuilderSimulation(
    raceBuilderInputs,
    raceProfile,
    strategyImportVersion,
    {
      pitStopTimeS:
        pitBoostScenarios.find(
          (scenario) => scenario.scenario === raceBuilderInputs.pitScenario,
        )?.stopTimeS ?? null,
      segmentEndSlapM,
    },
  );
  useEffect(() => {
    if (appliedStrategyVersion.current === strategyImportVersion) return;

    appliedStrategyVersion.current = strategyImportVersion;
    profileRequestVersion.current += 1;
    setRaceBuilderBaselineInputs(getRaceBuilderDefaults(strategyImport));
    setRaceProfile(null);
    setProfileError(null);
    setProfileLoading(false);
  }, [strategyImport, strategyImportVersion]);

  function updateRaceBuilderInput<Key extends keyof RaceBuilderFormState>(
    key: Key,
    value: RaceBuilderFormState[Key],
  ) {
    setRaceBuilderBaselineInputs((current) => ({
      ...current,
      [key]:
        key === "totalRaceEnergykWh" &&
        typeof value === "number" &&
        pitBoostEnergyAdjustmentKwh !== null
          ? value - pitBoostEnergyAdjustmentKwh
          : value,
    }));
  }

  async function handleGenerateProfile() {
    const {
      totalRaceEnergykWh,
      numberOfLaps,
      dataFile,
      ucProfile,
      lockingTargetkWh,
      maxUnderconsumptionkWh,
    } = raceBuilderInputs;
    if (
      raceEnergyError !== null ||
      !ucProfileValid ||
      totalRaceEnergykWh === "" ||
      numberOfLaps === "" ||
      !dataFile.trim()
    )
      return;

    builderSimulation.clearResult();
    const requestVersion = ++profileRequestVersion.current;
    setProfileError(null);
    setProfileLoading(true);
    try {
      const result = await client.query<{
        getRessRaceProfile: RaceProfileData;
      }>({
        query: QUERY_RESS_RACE_PROFILE,
        variables: {
          totalRaceEnergykWh,
          numberOfLaps,
          dataFile: dataFile.trim(),
          ucProfile,
          lockingTargetkWh: lockingTargetkWh === "" ? null : lockingTargetkWh,
          maxUnderconsumptionkWh,
        },
        fetchPolicy: "no-cache",
        context: { skipRetry: true },
      });
      if (requestVersion !== profileRequestVersion.current) return;
      if (result.error) throw result.error;
      if (!result.data?.getRessRaceProfile) {
        throw new Error("No race profile was returned");
      }
      const profile = result.data.getRessRaceProfile;
      setRaceProfile((current) => ({
        ...profile,
        dataFile: dataFile.trim(),
        totalRaceEnergykWh,
        numberOfLaps,
        ucProfile,
        lockingTargetkWh,
        maxUnderconsumptionkWh,
        laps: profile.laps.map((lap) => {
          const previous = current?.laps[lap.lapNumber - 1];
          return {
            ...lap,
            tow: previous?.tow ?? lap.tow,
          };
        }),
      }));
    } catch (error) {
      if (requestVersion !== profileRequestVersion.current) return;
      setProfileError(
        error instanceof Error
          ? error.message
          : "Could not generate race profile",
      );
    } finally {
      if (requestVersion === profileRequestVersion.current) {
        setProfileLoading(false);
      }
    }
  }

  function toggleProfileFlag(
    lapNumber: number,
    field: "attack" | "tow",
    checked: boolean,
  ) {
    setRaceProfile((current) =>
      current
        ? {
            ...current,
            laps: current.laps.map((lap) =>
              lap.lapNumber === lapNumber ? { ...lap, [field]: checked } : lap,
            ),
          }
        : null,
    );
  }

  const tCellMax = builderSimulation.result?.data.tCellMaxC ?? EMPTY_TRACE;
  const tCellMin = builderSimulation.result?.data.tCellMinC ?? EMPTY_TRACE;
  const pbms = builderSimulation.result?.data.pbmsKw ?? EMPTY_TRACE;

  useEffect(() => {
    temperatureChartRef.current?.resetZoom();
    powerChartRef.current?.resetZoom();
  }, [tCellMax, tCellMin, pbms]);

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: "TCell Max",
          data: tCellMax.map((temperature, index) => ({
            x: index * 0.1,
            y: temperature,
          })),
          borderColor: "#a18b6b",
          backgroundColor: "#a18b6b",
          borderWidth: 2,
          segment: {
            borderColor: (context: ScriptableLineSegmentContext) =>
              tCellMaxDerateStarts !== null &&
              (context.p0.parsed.y > tCellMaxDerateStarts ||
                context.p1.parsed.y > tCellMaxDerateStarts)
                ? "#ff3b3b"
                : "#a18b6b",
          },
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          showLine: true,
          tension: 0,
          fill: false,
        },
        {
          label: "TCell Min",
          data: tCellMin.map((temperature, index) => ({
            x: index * 0.1,
            y: temperature,
          })),
          borderColor: "#6fc7b5",
          backgroundColor: "#6fc7b5",
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          showLine: true,
          tension: 0,
          fill: false,
        },
        ...(tCellMaxDerateStarts === null
          ? []
          : [
              {
                label: `${tCellMaxDerateStarts}°C limit`,
                data: [
                  { x: 0, y: tCellMaxDerateStarts },
                  {
                    x: Math.max((tCellMax.length - 1) * 0.1, 1),
                    y: tCellMaxDerateStarts,
                  },
                ],
                borderColor: "#ff3b3b",
                borderWidth: 2,
                borderDash: [6, 6],
                pointRadius: 0,
                pointHoverRadius: 0,
                pointHitRadius: 0,
                showLine: true,
                tension: 0,
                fill: false,
              },
            ]),
      ],
    }),
    [tCellMax, tCellMin, tCellMaxDerateStarts],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: {
        zoom: {
          limits: {
            x: {
              min: "original" as const,
              max: "original" as const,
              minRange: 1,
            },
            y: { min: "original" as const, max: "original" as const },
          },
          pan: {
            enabled: true,
            mode: "xy" as const,
            modifierKey: "shift" as const,
          },
          zoom: {
            mode: "xy" as const,
            wheel: { enabled: true, modifierKey: "ctrl" as const },
            pinch: { enabled: true },
            drag: {
              enabled: true,
              threshold: 5,
              backgroundColor: "rgba(161, 139, 107, 0.2)",
              borderColor: "#a18b6b",
              borderWidth: 1,
            },
          },
        },
        datalabels: {
          display: false,
        },
        legend: {
          labels: { color: "#ffffff" },
        },
        title: {
          display: true,
          text: "Race Builder TCell Max / Min",
          color: "#ffffff",
        },
      },
      scales: {
        x: {
          type: "linear" as const,
          title: { display: true, text: "Time (s)", color: "#ffffff" },
          ticks: { color: "#ffffff" },
          grid: { color: "#505050" },
        },
        y: {
          title: { display: true, text: "Temperature (°C)", color: "#ffffff" },
          ticks: { color: "#ffffff" },
          grid: { color: "#505050" },
        },
      },
    }),
    [],
  );

  const powerChartData = useMemo(
    () => ({
      datasets: [
        {
          label: "PBMS",
          data: pbms.map((power, index) => ({
            x: index * 0.1,
            y: power,
          })),
          borderColor: "#6fa8dc",
          backgroundColor: "#6fa8dc",
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          showLine: true,
          tension: 0,
          fill: false,
        },
      ],
    }),
    [pbms],
  );

  const powerChartOptions = useMemo(
    () => ({
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          ...chartOptions.plugins.title,
          text: "Race Builder PBMS",
        },
      },
      scales: {
        ...chartOptions.scales,
        y: {
          ...chartOptions.scales.y,
          min: -800,
          max: 700,
          title: { display: true, text: "PBMS (kW)", color: "#ffffff" },
          ticks: {
            color: "#ffffff",
            callback: (value: string | number) =>
              value === 500 || value === -500 ? "" : value,
          },
        },
      },
    }),
    [chartOptions],
  );

  return (
    <AuthenticatedLayout title={"RESS"} showJaguarLogo={false}>
      <ImportsDataManager />
      <RaceDataDataManager />
      <RESSDataManager />

      <div className={styles.container} aria-busy={builderSimulation.loading}>
        <div className={styles.viewHeader}>
          <div className={styles.tabs} role="tablist" aria-label="RESS views">
            <button
              id="race-builder-tab"
              className={`${styles.tab} ${
                activeTab === "race-builder" ? styles.activeTab : ""
              }`}
              type="button"
              role="tab"
              aria-selected={activeTab === "race-builder"}
              aria-controls="race-builder-panel"
              onClick={() => setActiveTab("race-builder")}
            >
              Race Builder
            </button>
            <button
              id="plots-tab"
              className={`${styles.tab} ${
                activeTab === "plots" ? styles.activeTab : ""
              }`}
              type="button"
              role="tab"
              aria-selected={activeTab === "plots"}
              aria-controls="plots-panel"
              onClick={() => setActiveTab("plots")}
            >
              Plots
            </button>
          </div>
          {activeTab === "race-builder" && <RaceBuilderConfig />}
        </div>

        {activeTab === "race-builder" ? (
          <div
            id="race-builder-panel"
            className={styles.raceBuilderPanel}
            role="tabpanel"
            aria-labelledby="race-builder-tab"
          >
            <div
              className={`${styles.raceBuilderColumn} ${
                raceBuilderInputs.pitBoostRace
                  ? styles.pitBoostBuilderColumn
                  : ""
              }`}
            >
              <RaceBuilderInputs
                inputs={raceBuilderInputs}
                onChange={updateRaceBuilderInput}
                pitBoostScenarios={pitBoostScenarios}
                energyError={raceEnergyError}
              />
              <RaceProfile
                profile={raceProfile}
                loading={profileLoading}
                error={profileError}
                canGenerate={
                  raceEnergyError === null &&
                  ucProfileValid &&
                  raceBuilderInputs.dataFile.trim() !== "" &&
                  raceBuilderInputs.totalRaceEnergykWh !== "" &&
                  raceBuilderInputs.numberOfLaps !== ""
                }
                inputsChanged={
                  raceProfile !== null &&
                  raceProfileInputsChanged(raceProfile, raceBuilderInputs)
                }
                onGenerate={handleGenerateProfile}
                onToggle={toggleProfileFlag}
              />
            </div>
            <RaceBuilderSimulation
              simulation={builderSimulation}
              tCellMaxDerateStarts={tCellMaxDerateStarts}
              disabled={profileLoading}
            />
          </div>
        ) : (
          <div id="plots-panel" role="tabpanel" aria-labelledby="plots-tab">
            <div className={styles.plotGroups}>
              <section
                className={styles.plotGroup}
                aria-labelledby="race-builder-plots-title"
              >
                <h2 id="race-builder-plots-title">Race Builder</h2>
                <p className={styles.plotInteractionHint}>
                  Drag to zoom into a region. Shift + drag to pan. Ctrl + scroll
                  or pinch to zoom. Reset restores the full trace.
                </p>
                <div className={styles.chartPanel}>
                  <div className={styles.interactivePlot}>
                    <div
                      className={styles.plotControls}
                      role="group"
                      aria-label="TCell plot controls"
                    >
                      <button
                        type="button"
                        onClick={() => temperatureChartRef.current?.zoom(1.5)}
                        disabled={!tCellMax.length}
                      >
                        Zoom in
                      </button>
                      <button
                        type="button"
                        onClick={() => temperatureChartRef.current?.zoom(0.75)}
                        disabled={!tCellMax.length}
                      >
                        Zoom out
                      </button>
                      <button
                        type="button"
                        onClick={() => temperatureChartRef.current?.resetZoom()}
                        disabled={!tCellMax.length}
                      >
                        Reset
                      </button>
                    </div>
                    <div className={styles.chartCanvas}>
                      <Line
                        ref={temperatureChartRef}
                        data={chartData}
                        options={chartOptions}
                        role="img"
                        aria-label="Race Builder TCell maximum and minimum against time"
                      />
                    </div>
                  </div>
                  <div className={styles.interactivePlot}>
                    <div
                      className={styles.plotControls}
                      role="group"
                      aria-label="PBMS plot controls"
                    >
                      <button
                        type="button"
                        onClick={() => powerChartRef.current?.zoom(1.5)}
                        disabled={!pbms.length}
                      >
                        Zoom in
                      </button>
                      <button
                        type="button"
                        onClick={() => powerChartRef.current?.zoom(0.75)}
                        disabled={!pbms.length}
                      >
                        Zoom out
                      </button>
                      <button
                        type="button"
                        onClick={() => powerChartRef.current?.resetZoom()}
                        disabled={!pbms.length}
                      >
                        Reset
                      </button>
                    </div>
                    <div className={styles.chartCanvas}>
                      <Line
                        ref={powerChartRef}
                        data={powerChartData}
                        options={powerChartOptions}
                        role="img"
                        aria-label="Race Builder PBMS against time"
                      />
                    </div>
                  </div>
                </div>
              </section>
              <section
                className={styles.plotGroup}
                aria-labelledby="race-page-plots-title"
              >
                <h2 id="race-page-plots-title">Race Page</h2>
                <div className={styles.chartPanel}>
                  <div className={styles.chartCanvas}>
                    <OptimisationTCellGraph />
                  </div>
                  <div className={styles.chartCanvas}>
                    <OptimisationPbmsGraph />
                  </div>
                </div>
              </section>
            </div>
            <EnergySelectionGraphs />
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default RESSPage;
